import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../Provider';
import { useToast } from '../hooks/ToastContext';
import { FaTimes, FaBug } from 'react-icons/fa';
import { CustomButton6, CustomInputBox1, CustomTextArea } from '../Templates';
import Spinner from '../common/Spinner';
import './BugReportModal.css';

const BugReportModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.profile?.full_name || '',
    email: user?.profile?.email || '',
    description: ''
  });

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.description) {
      addToast({
        type: 'error',
        title: t('forms.validation.error'),
        description: t('forms.validation.all_fields_required')
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      addToast({
        type: 'error',
        title: t('forms.validation.error'),
        description: t('forms.validation.invalid_email')
      });
      return;
    }

    setLoading(true);

    try {
      // Get current page URL and browser info
      const pageUrl = window.location.href;
      const userAgent = navigator.userAgent;
      
      // Send to Cloudflare Worker
      const response = await fetch('https://contact-support-email-worker.davidskuzmans.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'bug',
          name: formData.name,
          email: formData.email,
          description: formData.description,
          pageUrl: pageUrl,
          userAgent: userAgent,
          language: i18n.language || 'lv'
        })
      });

      const data = await response.json();

      if (response.ok) {
        addToast({
          type: 'success',
          title: t('bugReport.success.title'),
          description: t('bugReport.success.message')
        });
        
        // Reset form and close modal
        setFormData({
          name: user?.profile?.full_name || '',
          email: user?.profile?.email || '',
          description: ''
        });
        onClose();
      } else {
        // Handle rate limiting
        if (response.status === 429) {
          addToast({
            type: 'error',
            title: t('bugReport.error.rate_limit_title'),
            description: t('bugReport.error.rate_limit_message')
          });
        } else {
          addToast({
            type: 'error',
            title: t('bugReport.error.title'),
            description: data.error || t('bugReport.error.message')
          });
        }
      }
    } catch (error) {
      console.error('Bug report error:', error);
      addToast({
        type: 'error',
        title: t('bugReport.error.title'),
        description: t('bugReport.error.network')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form when closing
      setFormData({
        name: user?.profile?.full_name || '',
        email: user?.profile?.email || '',
        description: ''
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bug-report-modal-overlay" onClick={handleClose}>
      <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bug-report-modal-header">
          <div className="bug-report-modal-title">
            <FaBug className="bug-icon" />
            <h2>{t('bugReport.title')}</h2>
          </div>
          <button 
            className="bug-report-modal-close" 
            onClick={handleClose}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </div>

        <div className="bug-report-modal-content">
          {loading ? (
            <div className="bug-report-loading">
              <Spinner size="medium" showText={true} customText={t('bugReport.sending')} />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <p className="bug-report-info">
                {t('bugReport.info')}
              </p>
              
              <div className="bug-report-form-group">
                <CustomInputBox1
                  type="text"
                  placeholder={t('bugReport.form.name')}
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="bug-report-form-group">
                <CustomInputBox1
                  type="email"
                  placeholder={t('bugReport.form.email')}
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="bug-report-form-group">
                <CustomTextArea
                  placeholder={t('bugReport.form.description')}
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  rows={4}
                  maxLength={500}
                  disabled={loading}
                />
              </div>
              
              <div className="bug-report-form-actions">
                <button 
                  type="button" 
                  className="bug-report-cancel-btn"
                  onClick={handleClose}
                  disabled={loading}
                >
                  {t('buttons.cancel')}
                </button>
                <CustomButton6
                  type="submit"
                  label={t('bugReport.form.submit')}
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BugReportModal;
